import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useLoggedInEmployeeId, useLoggedInUserRoles } from '~/components/Auth';
import { useApplicationConfig } from '~/data/applicationConfig';
import { fetchOrganizationUnits } from '~/data/orgunits';
import { BUUnitData } from '~/data/orgunits/types';
import { useOrgTree } from '~/data/orgunits/useOrgTree';
import { useLocalStorage } from '~/hooks/useLocalStorage';
import { sortBUUnitData } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/data';
import { AppDispatch } from '~/store';

export type NestedOrgTreeUnit = {
  unitId: number;
  unitName: string;
  unitNumber: string;
  hasConfigs: boolean;
  hasSubConfigs: boolean;
  unitDepth: number;
  parentUnitId: number | string;
  subUnits: NestedOrgTreeUnit[];
};

type PropsType = {
  unitIdsWithWorkflows: number[];
  onUnitSelect: (unitId: number) => void;
};

export const useWorkflowConfigUnitSelector = (props: PropsType) => {
  const { unitIdsWithWorkflows, onUnitSelect } = props;

  const { t } = useTranslation();
  const { applicationConfig } = useApplicationConfig();
  const dispatch: AppDispatch = useDispatch();
  const myRoles = useLoggedInUserRoles();
  const myId = useLoggedInEmployeeId();
  const { orgTree: orgTreeData } = useOrgTree();

  useEffect(() => {
    dispatch(fetchOrganizationUnits({ myRoles, myId }));
  }, [myRoles, myId, dispatch]);

  const primaryTreeUnits: BUUnitData[] = useMemo(() => {
    return (
      orgTreeData.find((tree) => tree.fTreeName === 'Primary')?.units || []
    );
  }, [orgTreeData]);

  const getNestedUnit = useCallback(
    (
      parentId: number | string,
      units: BUUnitData[],
      depth: number,
    ): NestedOrgTreeUnit[] => {
      const newDepth = depth + 1;
      return sortBUUnitData(
        units.filter((unit) => unit.fTreeParentUnitId === parentId),
      ).map((unit): NestedOrgTreeUnit => {
        const subs = getNestedUnit(unit.fTreeUnitId, units, newDepth);
        const unitId = parseInt(unit.fTreeUnitId);

        return {
          unitId,
          unitName: unit.fTreeUnitName,
          unitNumber: unit.fTreeUnitNumber,
          hasConfigs: unitIdsWithWorkflows.includes(unitId),
          hasSubConfigs:
            subs.filter((u) => u.hasConfigs || u.hasSubConfigs).length > 0,
          unitDepth: newDepth,
          parentUnitId: parseInt(unit.fTreeParentUnitId),
          subUnits: subs,
        };
      });
    },
    [unitIdsWithWorkflows],
  );

  // Org tree state
  const { orgTree, defaultTreeState } = useMemo(() => {
    const orgTreeUnits = getNestedUnit('#', primaryTreeUnits, 1);
    const orgTree: NestedOrgTreeUnit[] = [
      {
        unitId: -1,
        unitName: applicationConfig.fComName,
        unitNumber: t('unitGlobal'),
        hasConfigs: unitIdsWithWorkflows.includes(-1),
        hasSubConfigs:
          orgTreeUnits.filter((u) => u.hasConfigs || u.hasSubConfigs).length >
          0,
        unitDepth: 1,
        parentUnitId: -1,
        subUnits: orgTreeUnits,
      },
    ];

    return {
      orgTree: orgTree,
      defaultTreeState: [-1],
    };
  }, [
    t,
    primaryTreeUnits,
    getNestedUnit,
    unitIdsWithWorkflows,
    applicationConfig,
  ]);

  const [orgTreeState, setOrgTreeState] = useLocalStorage<number[]>(
    'workflowUnitSelectionTreeState',
    defaultTreeState,
  );

  const isUnitOpen = useCallback(
    (unitId: number) => {
      return orgTreeState.includes(unitId);
    },
    [orgTreeState],
  );

  const closeUnit = useCallback(
    (unitId: number) => {
      setOrgTreeState(orgTreeState.filter((uId) => uId !== unitId));
    },
    [setOrgTreeState, orgTreeState],
  );

  const openUnit = useCallback(
    (unitId: number) => {
      setOrgTreeState([...orgTreeState, unitId]);
    },
    [setOrgTreeState, orgTreeState],
  );

  const toggleUnit = useCallback(
    (unitId: number) => {
      if (isUnitOpen(unitId)) {
        closeUnit(unitId);
      } else {
        openUnit(unitId);
      }
    },
    [isUnitOpen, closeUnit, openUnit],
  );

  const handleUnitSelect = useCallback(
    (unitId: number) => {
      const foundUnit = primaryTreeUnits.find(
        (unit) => unit.fTreeUnitId === unitId.toString(),
      );

      // Unit exists or is Global
      if (!!foundUnit || unitId === -1) {
        onUnitSelect(unitId);
      }
    },
    [primaryTreeUnits, onUnitSelect],
  );

  return {
    orgTree,
    orgTreeState: {
      openUnits: orgTreeState,
      isUnitOpen,
      closeUnit,
      openUnit,
      toggleUnit,
    },
    handleUnitSelect,
  };
};
